import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const DataCacheContext = createContext();

export const useDataCache = () => {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error('useDataCache must be used within a DataCacheProvider');
  }
  return context;
};

export const DataCacheProvider = ({ children }) => {
  const [bookCache, setBookCache] = useState({});
  const [companyCache, setCompanyCache] = useState({});
  const [cacheTimestamps, setCacheTimestamps] = useState({});

  // Cache expiration time (5 minutes)
  const CACHE_EXPIRY = 5 * 60 * 1000;

  // Load cache from localStorage on mount
  useEffect(() => {
    try {
      const savedBookCache = localStorage.getItem('bookCache');
      const savedCompanyCache = localStorage.getItem('companyCache');
      const savedTimestamps = localStorage.getItem('cacheTimestamps');

      if (savedBookCache) {
        setBookCache(JSON.parse(savedBookCache));
      }
      if (savedCompanyCache) {
        setCompanyCache(JSON.parse(savedCompanyCache));
      }
      if (savedTimestamps) {
        setCacheTimestamps(JSON.parse(savedTimestamps));
      }
    } catch (error) {
      console.error('Error loading cache from localStorage:', error);
    }
  }, []);

  // Save cache to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('bookCache', JSON.stringify(bookCache));
    } catch (error) {
      console.error('Error saving book cache to localStorage:', error);
    }
  }, [bookCache]);

  useEffect(() => {
    try {
      localStorage.setItem('companyCache', JSON.stringify(companyCache));
    } catch (error) {
      console.error('Error saving company cache to localStorage:', error);
    }
  }, [companyCache]);

  useEffect(() => {
    try {
      localStorage.setItem('cacheTimestamps', JSON.stringify(cacheTimestamps));
    } catch (error) {
      console.error('Error saving cache timestamps to localStorage:', error);
    }
  }, [cacheTimestamps]);

  // Check if cache is valid
  const isCacheValid = (project, dataType) => {
    const key = `${project}_${dataType}`;
    const timestamp = cacheTimestamps[key];
    if (!timestamp) return false;
    
    const now = Date.now();
    return (now - timestamp) < CACHE_EXPIRY;
  };

  // Get cached data
  const getCachedData = (project, dataType) => {
    const key = `${project}_${dataType}`;
    if (dataType === 'books' || dataType === 'books_list' || dataType === 'books_detail') {
      return bookCache[key] || [];
    } else if (dataType === 'companies') {
      return companyCache[key] || [];
    }
    return [];
  };

  // Set cached data
  const setCachedData = (project, dataType, data) => {
    const key = `${project}_${dataType}`;
    const timestamp = Date.now();
    
    if (dataType === 'books' || dataType === 'books_list' || dataType === 'books_detail') {
      setBookCache(prev => ({ ...prev, [key]: data }));
    } else if (dataType === 'companies') {
      setCompanyCache(prev => ({ ...prev, [key]: data }));
    }
    
    setCacheTimestamps(prev => ({ ...prev, [key]: timestamp }));
  };

  // Find specific item in cache
  const findCachedItem = (project, dataType, itemCode) => {
    const cachedData = getCachedData(project, dataType);
    return cachedData.find(item => item.code === itemCode);
  };

  // Clear cache for a specific project/dataType
  const clearCache = (project, dataType) => {
    const key = `${project}_${dataType}`;
    
    if (dataType === 'books' || dataType === 'books_list' || dataType === 'books_detail') {
      setBookCache(prev => {
        const newCache = { ...prev };
        delete newCache[key];
        return newCache;
      });
    } else if (dataType === 'companies') {
      setCompanyCache(prev => {
        const newCache = { ...prev };
        delete newCache[key];
        return newCache;
      });
    }
    
    setCacheTimestamps(prev => {
      const newTimestamps = { ...prev };
      delete newTimestamps[key];
      return newTimestamps;
    });
  };

  // Clear all cache
  const clearAllCache = () => {
    setBookCache({});
    setCompanyCache({});
    setCacheTimestamps({});
    localStorage.removeItem('bookCache');
    localStorage.removeItem('companyCache');
    localStorage.removeItem('cacheTimestamps');
  };

  const value = useMemo(() => ({
    isCacheValid,
    getCachedData,
    setCachedData,
    findCachedItem,
    clearCache,
    clearAllCache
  }), [bookCache, companyCache, cacheTimestamps]);

  return (
    <DataCacheContext.Provider value={value}>
      {children}
    </DataCacheContext.Provider>
  );
};
